import React, { useMemo } from "react";
import type { LeaderboardEntry } from "../../types/core";
import { toOrgDateKey } from "../../lib/timezone";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { toDisplayCurrency } from "../../utils/formatters";

// =============================================================================
// PERFORMANCE SNIPPET — Compact Table, Numbers-First
// =============================================================================
// Table layout: one header row, then one row per rep.
// Compact cash formatting ($174K not $174,043).
// No sparklines — they're noise at this column width.
// =============================================================================

type Sector = "setter" | "closer" | "dialer";

interface PerformanceSnippetProps {
  leaderboard: LeaderboardEntry[];
  dateRange: { start: Date | null; end: Date | null };
  sector: Sector;
  bare?: boolean;
}

interface AggRep {
  rep_id: string;
  rep_name: string;
  calls_booked: number;
  shows: number;
  no_shows: number;
  losses: number;
  show_rate: number;
  cc_total: number;
  wins: number;
  calls_taken: number;
  close_rate: number;
  daily_dials: number;
  pending_closes: number;
}

// Compact cash: $174,043 → $174K, $1,200 → $1.2K, $800 → $800
function compactCash(v: number): string {
  const d = toDisplayCurrency(v); // exec USD->AUD toggle (identity in USD mode)
  if (d >= 1000) return `$${Math.round(d / 1000)}K`;
  return `$${d.toLocaleString()}`;
}

const SECTOR_CONFIG: Record<
  Sector,
  {
    title: string;
    viewAllLink?: string;
    sortKey: keyof AggRep;
    columns: { key: string; label: string; width?: string }[];
  }
> = {
  closer: {
    title: "Closer Performance",
    sortKey: "cc_total",
    columns: [
      { key: "shows", label: "Shows" },
      { key: "wins", label: "Deals" },
      { key: "losses", label: "Lost" },
      { key: "cc_total", label: "Cash", width: "30%" },
    ],
  },
  setter: {
    title: "Setter Performance",
    viewAllLink: "/admin/dm-analytics",
    sortKey: "calls_booked",
    columns: [
      { key: "calls_booked", label: "Pre-Calls" },
      { key: "shows", label: "Shown" },
      { key: "losses", label: "Lost" },
      { key: "cc_total", label: "Cash", width: "30%" },
    ],
  },
  dialer: {
    title: "Dialer Performance",
    sortKey: "cc_total",
    columns: [
      { key: "daily_dials", label: "Dials" },
      { key: "calls_booked", label: "Pre-Calls" },
      { key: "shows", label: "Shown" },
      { key: "cc_total", label: "Cash", width: "30%" },
    ],
  },
};

function getColor(_key: string, _rep: AggRep): string {
  return "var(--glass-text)";
}

function fmtVal(key: string, rep: AggRep): string {
  if (key === "show_rate") return `${Math.min(Math.round(rep.show_rate * 100), 100)}%`;
  if (key === "close_rate") return `${Math.round(rep.close_rate * 100)}%`;
  if (key === "cc_total") return compactCash(rep.cc_total);
  const v = rep[key as keyof AggRep];
  return typeof v === "number" ? v.toLocaleString() : String(v);
}

const PerformanceSnippet: React.FC<PerformanceSnippetProps> = ({
  leaderboard,
  dateRange,
  sector,
  bare = false,
}) => {
  const config = SECTOR_CONFIG[sector];
  const { ref: revealRef, revealed } = useScrollReveal<HTMLDivElement>();

  const reps = useMemo(() => {
    const startStr = dateRange?.start ? toOrgDateKey(dateRange.start) : "";
    const endStr = dateRange?.end ? toOrgDateKey(dateRange.end) : "9999-12-31";
    const entries = leaderboard.filter(
      (e) => e.sector === sector && e.date >= startStr && e.date <= endStr
    );
    if (entries.length === 0) return [];

    const byRep = new Map<string, LeaderboardEntry[]>();
    for (const e of entries) {
      const arr = byRep.get(e.rep_id);
      if (arr) arr.push(e);
      else byRep.set(e.rep_id, [e]);
    }

    const result: AggRep[] = [];
    byRep.forEach((entries, repId) => {
      result.push({
        rep_id: repId,
        rep_name: entries[0].rep_name,
        calls_booked: entries.reduce((s, e) => s + e.calls_booked, 0),
        shows: entries.reduce((s, e) => s + e.shows, 0),
        no_shows: entries.reduce((s, e) => s + e.no_shows, 0),
        losses: entries.reduce((s, e) => s + (e.losses || 0), 0),
        show_rate: (() => {
          const sh = entries.reduce((s, e) => s + e.shows, 0);
          const ns = entries.reduce((s, e) => s + e.no_shows, 0);
          const orphans = entries.reduce((s, e) => s + (e.orphan_precalls || 0), 0);
          const denom = sh + ns + orphans;
          return denom > 0 ? sh / denom : 0;
        })(),
        cc_total: entries.reduce((s, e) => s + (e.cc_total || 0), 0),
        wins: entries.reduce((s, e) => s + (e.wins || 0), 0),
        pending_closes: entries.reduce((s, e) => s + (e.pending_closes || 0), 0),
        calls_taken: entries.reduce((s, e) => s + (e.calls_taken || 0), 0),
        // PROVISIONAL close rate per /metrics doctrine (2026-06-19): numerator = wins + pending_closes
        // (pending_close lifts numerator only). Denominator (shows) unchanged; displayed wins/deals stay PIF+CPP.
        close_rate: (() => {
          const sh = entries.reduce((s, e) => s + (e.shows || 0), 0);
          const w = entries.reduce((s, e) => s + (e.wins || 0), 0);
          const pc = entries.reduce((s, e) => s + (e.pending_closes || 0), 0);
          return sh > 0 ? (w + pc) / sh : 0;
        })(),
        daily_dials: entries.reduce((s, e) => s + (e.daily_dials || 0), 0),
      });
    });

    result.sort((a, b) => (b[config.sortKey] as number) - (a[config.sortKey] as number));
    return result;
  }, [leaderboard, sector, config, dateRange]);

  if (reps.length === 0) {
    return (
      <div className={bare ? "flex flex-col" : "liquid-glass p-4 flex flex-col h-full"}>
        {/* No title — sector is obvious from leaderboard above */}
        <div className="flex-1 flex items-center justify-center">
          <p className="token-13 text-glass-muted">No {sector} data this period</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={revealRef}
      className={`${bare ? "flex flex-col h-full" : "liquid-glass anim-lift p-4 flex flex-col h-full"} ${
        revealed ? "reveal-active" : "reveal-pending"
      }`}
    >
      {/* No header — leaderboard card above already has "View all →" */}

      {/* Compact table */}
      <table className="perf-table w-full" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "28%" }} />
          {config.columns.map((col) => (
            <col key={col.key} style={{ width: col.width || `${72 / config.columns.length}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="token-10u text-left pb-2 px-2" />
            {config.columns.map((col) => (
              <th key={col.key} className="token-10u text-right pb-2 px-2">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reps.map((rep, idx) => (
            <tr
              key={rep.rep_id}
              className={`reveal-row ${idx > 0 ? "perf-row-border" : ""}`}
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <td className="perf-name py-2.5 px-2">{rep.rep_name.split(" ")[0]}</td>
              {config.columns.map((col) => (
                <td key={col.key} className="perf-val py-2.5 px-2 text-right">
                  <span style={{ color: getColor(col.key, rep) }}>{fmtVal(col.key, rep)}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Coaching flags */}
      {sector === "closer" &&
        reps.some((r) => r.calls_taken > 0 && r.wins === 0 && r.close_rate === 0) && (
          <div className="mt-3 token-11 px-2.5 py-1.5 rounded bg-red-500/10 text-red-400 font-medium">
            Missing SCO:{" "}
            {reps
              .filter((r) => r.calls_taken > 0 && r.wins === 0 && r.close_rate === 0)
              .map((r) => r.rep_name.split(" ")[0])
              .join(", ")}
          </div>
        )}
    </div>
  );
};

export default PerformanceSnippet;
